import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import { TareaItem, TareaService } from "../../../../generated/grpc/configuracion_tarea_pb";
import { grpcTareaDatabase } from "../database/GrpcTareaDatabase";
import { serviceSync } from "../../../../base/services/ServiceSync";
import { TareaResponse } from "../response/TareaResponse";

/** Variante gRPC-Web de `TareaGetListByClienteIdService` (ver ese archivo).
 *  Devuelve `TareaResponse[]` -mismo shape que ya usa el resto de la app-,
 *  filtrado por cliente igual que la variante REST (globales + exclusivas). */
class TareaGrpcService {
  constructor(private readonly _clienteId: string) {}

  async getListAsync(): Promise<TareaResponse[]> {
    await this._syncAsync();
    const items = await grpcTareaDatabase.getListAsync<TareaItem>();
    return items
      .map(toTareaResponse)
      .filter((t) => t.clienteId === this._clienteId || t.clienteId == null);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:tareas:${this._clienteId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:tareas:${this._clienteId}`, async () => {
      const ultimaSincronizacion = await grpcTareaDatabase.getUltimaSincronizacionAsync(
        this._clienteId,
      );
      const client = createClient(TareaService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByClienteId({
          clienteId: this._clienteId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcTareaDatabase.putDeltaAsync(
        this._clienteId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por clienteId (comparte cooldown/caché,
 *  mismo criterio que `fx_createTareaGetListByClienteIdService`). */
export const fx_createTareaGrpcService = createCachedFactory(
  (clienteId: string) => new TareaGrpcService(clienteId),
);

/** Adapta un `TareaItem` (gRPC) al shape `TareaResponse` (REST). Exportada
 *  porque los servicios de guardado/borrado también la necesitan. */
export function toTareaResponse(tarea: TareaItem): TareaResponse {
  return {
    id: tarea.id,
    isEliminado: tarea.eliminado,
    clienteId: tarea.clienteId ?? null,
    nombre: tarea.nombre,
  };
}
